import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';

const COLLECTION_KEYS = ['teams', 'seasonTeams', 'players', 'games'];

function parseDocs(key, snap) {
  if (key === 'players') {
    return snap.docs.map(doc => doc.data());
  }
  if (key === 'games') {
    const games = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate ? doc.data().date.toDate() : doc.data().date
    }));
    games.sort((a, b) => a.date - b.date);
    return games;
  }
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

const LeagueDataContext = createContext({
  teams: [],
  seasonTeams: [],
  players: [],
  games: [],
  loaded: { teams: false, seasonTeams: false, players: false, games: false },
  ensureLeagueData: () => {},
  refreshLeagueData: () => Promise.resolve()
});

export function LeagueDataProvider({ children }) {
  const [data, setData] = useState({ teams: [], seasonTeams: [], players: [], games: [] });
  const [loaded, setLoaded] = useState({ teams: false, seasonTeams: false, players: false, games: false });
  const inFlight = useRef({});

  const fetchKey = useCallback((key) => {
    if (inFlight.current[key]) return inFlight.current[key];

    const promise = getDocs(collection(db, key))
      .then(snap => {
        setData(prev => ({ ...prev, [key]: parseDocs(key, snap) }));
      })
      .catch(err => {
        console.error(`Failed to load ${key}:`, err);
      })
      .finally(() => {
        setLoaded(prev => ({ ...prev, [key]: true }));
        delete inFlight.current[key];
      });

    inFlight.current[key] = promise;
    return promise;
  }, []);

  // Only fetch each collection the first time a page actually needs it.
  const ensureLeagueData = useCallback((keys) => {
    keys.forEach(key => {
      if (!loaded[key] && !inFlight.current[key]) {
        fetchKey(key);
      }
    });
  }, [loaded, fetchKey]);

  const refreshLeagueData = useCallback((keys = COLLECTION_KEYS) => {
    return Promise.all(keys.map(key => fetchKey(key)));
  }, [fetchKey]);

  return (
    <LeagueDataContext.Provider value={{ ...data, loaded, ensureLeagueData, refreshLeagueData }}>
      {children}
    </LeagueDataContext.Provider>
  );
}

// Pass the list of collections this component needs, e.g. useLeagueData(['teams', 'games']).
// Collections are fetched lazily on first use and cached for the session.
export function useLeagueData(keys = []) {
  const ctx = useContext(LeagueDataContext);
  const keysKey = keys.join(',');

  useEffect(() => {
    if (keys.length > 0) {
      ctx.ensureLeagueData(keys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysKey]);

  const dataLoaded = keys.length === 0 || keys.every(key => ctx.loaded[key]);

  return { ...ctx, dataLoaded };
}
